import { useMemo } from 'react';
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { ProductType } from '../utils/productType';
import { useLocation, useParams } from 'quartz-routing/react-router';
import { Paths } from '../constants/paths';
const VALID_PRODUCT_TYPES = new Set<string>([
  'APP',
  'AGENT',
  'SOLUTIONS_PARTNER_PROFILE',
  'WORKFLOW_TEMPLATE',
]);

export function useProductTypeSearchParams(): ProductType[] {
  const { getList } = useSearchParams();

  return getList('PRODUCT_TYPE').filter((v): v is ProductType =>
    VALID_PRODUCT_TYPES.has(v)
  );
}

/**
 * @deprecated Prefer `useProductTypes()`, which returns an array of
 * product types instead of a singular one (useful for situations that apply to both apps and agents),
 * and is more intelligent than just the param.
 */
export function useProductTypeSearchParam(): ProductType | undefined {
  const productTypes = useProductTypeSearchParams();
  const location = useLocation();

  if (location.pathname === Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS) {
    return 'SOLUTIONS_PARTNER_PROFILE';
  }

  if (
    location.pathname === Paths.WORKFLOW_TEMPLATES ||
    location.pathname.startsWith(`${Paths.WORKFLOW_TEMPLATES}/`)
  ) {
    return 'WORKFLOW_TEMPLATE';
  }

  const productType = productTypes.length === 1 ? productTypes[0] : undefined;

  return productType;
}

/**
 * The definitive source of truth on which product types are currently applicable,
 * based on the pathname, search params, and gates.
 */
export function useProductTypes(): ProductType[] {
  const location = useLocation();
  const params = useSearchParams();
  const isCollection = !!useParams<{ collectionSlug: string }>().collectionSlug;

  const productTypeParam = params.get('PRODUCT_TYPE', {
    allowedValues: [
      'APP',
      'AGENT',
      'SOLUTIONS_PARTNER_PROFILE',
      'WORKFLOW_TEMPLATE',
    ] as const,
  });

  return useMemo(() => {
    // Currently, we only support apps for collections
    if (isCollection) {
      return ['APP'];
    }

    // "All Products" page
    if (location.pathname === Paths.SEARCH_RESULTS_PRODUCTS) {
      return productTypeParam
        ? [productTypeParam]
        : ['APP', 'AGENT', 'WORKFLOW_TEMPLATE'];
    }

    // Workflow templates landing page and search proxy routes
    if (
      location.pathname === Paths.WORKFLOW_TEMPLATES ||
      location.pathname.startsWith(`${Paths.WORKFLOW_TEMPLATES}/`)
    ) {
      return ['WORKFLOW_TEMPLATE'];
    }

    // Solutions partners homepage OR "All Solutions Partners" page
    if (
      location.pathname === Paths.SOLUTIONS_PARTNERS ||
      location.pathname === Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS
    ) {
      return ['SOLUTIONS_PARTNER_PROFILE'];
    }

    // Apps homepage
    if (location.pathname === Paths.APPS) return ['APP'];

    // Agents homepage
    if (location.pathname === Paths.AGENTS) return ['AGENT'];

    // "All" search tab
    if (location.pathname === Paths.SEARCH_RESULTS && !productTypeParam) {
      return ['APP', 'AGENT', 'SOLUTIONS_PARTNER_PROFILE', 'WORKFLOW_TEMPLATE'];
    }

    return productTypeParam ? [productTypeParam] : [];
  }, [isCollection, location.pathname, productTypeParam]);
}
